const PERSIST_STRATEGY: 'memory' | 'sessionStorage' | 'localStorage' = 'sessionStorage';

function storage() {
  if (PERSIST_STRATEGY === 'memory') return undefined;
  if (PERSIST_STRATEGY === 'localStorage') return window.localStorage;
  return window.sessionStorage;
}

// In-memory cache
const inMemory: Record<string, { value: string; expiry: number }> = {};

export const tokenManager = {
  /** Initialize from storage on first import */
  init() {
    const store = storage();
    if (!store) return;

    for (let i = 0; i < store.length; i++) {
      const key = store.key(i);
      if (!key || !key.endsWith('_value')) continue;

      const baseKey = key.replace('_value', '');
      const value = store.getItem(`${baseKey}_value`);
      const expiryStr = store.getItem(`${baseKey}_expiry`);
      const expiry = expiryStr ? parseInt(expiryStr, 10) : 0;

      if (value && Date.now() < expiry) {
        inMemory[baseKey] = { value, expiry };
      } else {
        this.clear(baseKey);
      }
    }
  },

  /** Set key with value and expiration time (in ms) */
  set(key: string, value: string, expiresInMs: number) {
    const expiry = Date.now() + expiresInMs;
    inMemory[key] = { value, expiry };

    const store = storage();
    if (store) {
      store.setItem(`${key}_value`, value);
      store.setItem(`${key}_expiry`, expiry.toString());
    }
  },

  /** Get value if not expired, else null */
  get(key: string): string | null {
    const entry = inMemory[key];

    if (entry && Date.now() < entry.expiry) {
      return entry.value;
    }

    this.clear(key);
    return null;
  },

  /** Clear a specific key */
  clear(key: string) {
    delete inMemory[key];

    const store = storage();
    if (store) {
      store.removeItem(`${key}_value`);
      store.removeItem(`${key}_expiry`);
    }
  },

  /** Clear all keys */
  clearAll() {
    const store = storage();
    for (const key in inMemory) {
      this.clear(key);
    }

    if (store) {
      const keysToClear: string[] = [];

      for (let i = 0; i < store.length; i++) {
        const key = store.key(i);
        if (key?.endsWith('_value') || key?.endsWith('_expiry')) {
          keysToClear.push(key);
        }
      }

      for (const k of keysToClear) {
        store.removeItem(k);
      }
    }

    for (const key in inMemory) {
      delete inMemory[key];
    }
  },
};

tokenManager.init();
